import { HttpClient } from '@angular/common/http';
import { BreakpointObserverService, HREF, OverlayService, UserService } from '@core';
import { CartListComponent } from '@shared';
import type { ProductDetails, ProductDetailsCart } from '@models';
import { inject, Injectable } from '@angular/core';
import { Observable, EMPTY, throwError, timer } from 'rxjs';
import { switchMap, tap, catchError, filter, map, expand, take, ignoreElements } from 'rxjs/operators';
import { CartDataService } from './cart-data.service';
import { CartRelationshipService } from './cart-relationship.service';
import { OrderLimitService } from './order-limit.service';
import { StockLimitService } from './stock-limit.service';
import { outOfStockItemsFrom } from '../utils/out-of-stock.utils';
import type { OverlayConfig } from '@angular/cdk/overlay';
import { ENV } from '@di-tokens';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly cartDataService = inject(CartDataService);
  private readonly cartRelationshipService = inject(CartRelationshipService);
  private readonly orderLimitService = inject(OrderLimitService);
  private readonly stockLimitService = inject(StockLimitService);
  private readonly overlayService = inject(OverlayService);
  private readonly isTablet = inject(BreakpointObserverService).isTablet;
  private readonly isMobile = inject(BreakpointObserverService).isMobile;
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENV);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);

  addCartProduct(
    product: ProductDetails,
    { autoFocusActions = false }: { autoFocusActions?: boolean } = {},
    variantSelection: any | null = null,
    relatedLineNumber?: number,
  ): Observable<ProductDetailsCart> {
    // No state shipping restriction for the event (US-02.1 AC07): only a loaded player can add.
    if (!this.userService.user()) {
      return EMPTY;
    }

    // FCR 19.1 AC01 / RID-1352 — the cap belongs on the action, not only on the button rendering
    // it. Every click adds one unit, and a click that beat the cart refresh would otherwise
    // still reach `cart/add`. The message is already on screen (PDP notice / card tooltip), so
    // the call is just dropped.
    if (this.orderLimitService.isSkuAtLimit(product.productId)) {
      return EMPTY;
    }

    // RCP-458 DoD10 — the cart already holds all the available units of this SKU. Product cards don't
    // disable their button for it, so say so instead of sending a request bound to fail.
    if (this.stockLimitService.isSkuStockCapped(product.productId)) {
      this.stockLimitService.notifyOutOfStock([product]);
      return EMPTY;
    }

    const payload: any = {
      productId: product.productId,
    };
    if (variantSelection) {
      payload.variantSelection = variantSelection;
    }
    // Only include relatedLineNumber if it's a valid positive number
    // This prevents null, undefined, or invalid values (like 0) from being sent
    if (relatedLineNumber !== undefined && this.cartRelationshipService.isValidLineNumber(relatedLineNumber)) {
      payload.relatedLineNumber = relatedLineNumber;
    } else if (relatedLineNumber !== undefined) {
      // Log error if relatedLineNumber was provided but is invalid
      console.error('Cannot add product: relatedLineNumber is invalid', {
        relatedLineNumber,
        productId: product.productId,
      });
      // Do NOT proceed with adding product if relatedLineNumber is invalid
      return throwError(() => new Error('Invalid relatedLineNumber'));
    }

    return this.http.post(`${this.env.remoteApi}/cart/add`, payload).pipe(
      tap(() => {
        if (!this.router.url?.includes(HREF.CART)) {
          this.toggleCartDropdown(autoFocusActions);
        }
      }),
      switchMap(() => this.cartDataService.fetchProductCart()),
      catchError((error) => this.recoverFromOutOfStock(error)),
    );
  }

  toggleCartDropdown(autoFocusActions = false): void {
    let config: OverlayConfig | null;
    if (this.isTablet()) {
      const position = this.overlayService.overlay.position().global().bottom('0').centerHorizontally();
      config = {
        positionStrategy: position,
        hasBackdrop: true,
        backdropClass: 'lib-cart-backdrop',
        scrollStrategy: this.overlayService.overlay.scrollStrategies.block(),
        width: '600px',
      };
    } else if (this.isMobile()) {
      const position = this.overlayService.overlay.position().global().bottom('0');
      config = {
        positionStrategy: position,
        hasBackdrop: true,
        backdropClass: 'lib-cart-backdrop',
        scrollStrategy: this.overlayService.overlay.scrollStrategies.block(),
        width: '100vw',
      };
    } else {
      const position = this.overlayService.overlay.position().global().top('64px').right('28px');
      config = {
        positionStrategy: position,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        width: '450px',
      };
    }
    const ref = this.overlayService.open(CartListComponent, undefined, config);
    if (ref) {
      ref.instance.autoFocusActions = autoFocusActions;
    }
  }

  getCartItemCount(): number {
    const cart = this.cartDataService.productDetailsCart();
    return cart?.products?.length || 0;
  }

  getUpdatedCartThenAddAppleCare(
    mainProductId: number,
    appleCareProduct: ProductDetails,
    maxRetries = 3,
  ): Observable<ProductDetailsCart> {
    // Fetch the latest cart state to get the actual lineNumber of the main product
    // Use expand to retry with delay until we find the product or exhaust retries
    let retryCount = 0;

    return this.cartDataService.fetchProductCart().pipe(
      map((cart) => {
        const mainProduct = cart.products.find((p) => p.productId === mainProductId);

        // Validate that parent product exists in cart AND has a valid positive lineNumber
        if (mainProduct && this.cartRelationshipService.isValidLineNumber(mainProduct.lineNumber)) {
          return { success: true, lineNumber: mainProduct.lineNumber, cart };
        }

        // Return failure state to indicate retry is needed
        return { success: false, lineNumber: null, cart };
      }),
      expand((result) => {
        if (result.success || retryCount >= maxRetries) {
          // Stop retrying - either success or max retries reached
          return EMPTY;
        }

        retryCount++;
        // Retry after delay with exponential backoff
        return timer(300 * retryCount).pipe(
          switchMap(() => this.cartDataService.fetchProductCart()),
          map((cart) => {
            const mainProduct = cart.products.find((p) => p.productId === mainProductId);
            if (mainProduct && this.cartRelationshipService.isValidLineNumber(mainProduct.lineNumber)) {
              return { success: true, lineNumber: mainProduct.lineNumber, cart };
            }
            return { success: false, lineNumber: null, cart };
          }),
        );
      }),
      filter((result): result is { success: true; lineNumber: number; cart: ProductDetailsCart } => result.success),
      take(1),
      switchMap((result) => {
        // Add AppleCare with the correct relatedLineNumber - suppress dropdown since main product already showed it
        return this.addCartProductSuppressDropdown(
          appleCareProduct,
          { autoFocusActions: false },
          null,
          result.lineNumber,
        );
      }),
      catchError((error) => {
        // After all retries failed, log error and don't add AppleCare without relatedLineNumber
        console.error(
          `Failed to add AppleCare after ${maxRetries} retries: Main product not found in cart or has invalid lineNumber`,
          { mainProductId, error, retryCount },
        );
        // Do NOT add AppleCare without relatedLineNumber - it would create orphaned AppleCare
        return throwError(() => error);
      }),
    );
  }
  // Add a method that always suppresses the dropdown for AppleCare additions
  addCartProductSuppressDropdown(
    product: ProductDetails,
    { autoFocusActions: _autoFocusActions = false }: { autoFocusActions?: boolean } = {}, // eslint-disable-line @typescript-eslint/no-unused-vars
    variantSelection: any | null = null,
    relatedLineNumber?: number,
  ): Observable<ProductDetailsCart> {
    // No state shipping restriction for the event (US-02.1 AC07): only a loaded player can add.
    if (!this.userService.user()) {
      return EMPTY;
    }

    const payload: any = {
      productId: product.productId,
    };
    if (variantSelection) {
      payload.variantSelection = variantSelection;
    }
    // Only include relatedLineNumber if it's a valid positive number
    // This prevents null, undefined, or invalid values (like 0) from being sent
    if (relatedLineNumber !== undefined && this.cartRelationshipService.isValidLineNumber(relatedLineNumber)) {
      payload.relatedLineNumber = relatedLineNumber;
    } else if (relatedLineNumber !== undefined) {
      // Log error if relatedLineNumber was provided but is invalid
      console.error('Cannot add product: relatedLineNumber is invalid', {
        relatedLineNumber,
        productId: product.productId,
      });
      // Do NOT proceed with adding product if relatedLineNumber is invalid
      return throwError(() => new Error('Invalid relatedLineNumber'));
    }

    return this.http.post(`${this.env.remoteApi}/cart/add`, payload).pipe(
      switchMap(() => this.cartDataService.fetchProductCart()),
      catchError((error) => this.recoverFromOutOfStock(error)),
    );
  }

  /**
   * RCP-458 DoD10 — `cart/add` refused beyond the available count (`409`): name the product and
   * re-sync the cart, so every "Add to cart" caller gets the same outcome. Completes without a value:
   * nothing was added, so follow-ups such as attaching AppleCare must not run. Other errors propagate.
   */
  private recoverFromOutOfStock(error: unknown): Observable<never> {
    const items = outOfStockItemsFrom(error);
    if (!items) return throwError(() => error);

    this.stockLimitService.notifyOutOfStock(items);
    return this.cartDataService.fetchProductCart().pipe(ignoreElements());
  }
}
